<script lang="ts" setup>
import { storeToRefs } from 'pinia';
import {
  computed,
  onUnmounted,
  provide,
  ref,
} from 'vue';

import MessageActions from '#nora-chat-ui/components/open-conversations/messages/message-actions/MessageActions.vue';
import type { MessageState } from '#nora-chat-ui/states';
import { useConversationMessageStore } from '#nora-chat-ui/stores/conversation/message/conversation-message-store';

interface ViewMessageProps {
  messageState: MessageState;
  maxReactions: number;
}

const props = defineProps<ViewMessageProps>();

const conversationMessageStore = useConversationMessageStore();

// Keep the message actions open briefly when the mouse moves from the message.
const hideDelayMs = 350;
let hideActionButtonsTimeout: ReturnType<typeof setTimeout> | undefined;

// Small delay before showing the message actions
const showDelayMs = 300;
let showActionButtonsTimeout: ReturnType<typeof setTimeout> | undefined;

const { activeMessageActionsId, openContextMenuId } = storeToRefs(
  conversationMessageStore,
);
const actionButtonsVisible = computed(() =>
  activeMessageActionsId.value === props.messageState.message.id,
);
const isMessageHovered = ref(false);

function cancelHideActionButtons() {
  if (hideActionButtonsTimeout) {
    clearTimeout(hideActionButtonsTimeout);
    hideActionButtonsTimeout = undefined;
  }
}

function cancelShowActionButtons() {
  if (showActionButtonsTimeout) {
    clearTimeout(showActionButtonsTimeout);
    showActionButtonsTimeout = undefined;
  }
}

function showActionButtons() {
  showActionButtonsTimeout = undefined;

  if (
    !isMessageHovered.value
    || props.messageState.message.deletedAt
    || openContextMenuId.value
  ) {
    return;
  }

  conversationMessageStore.setActiveMessageActionsId(
    props.messageState.message.id,
  );
}

const optionMenuOpen = ref(false);

function handleOptionMenuToggled(isOpen: boolean) {
  optionMenuOpen.value = isOpen;
  if (isOpen) {
    conversationMessageStore.setOpenContextMenuId(
      props.messageState.message.id,
    );
  }
  else {
    conversationMessageStore.setOpenContextMenuId(undefined);
    conversationMessageStore.setActiveMessageActionsId(undefined);
  }
}

function onMouseLeave() {
  isMessageHovered.value = false;
  cancelShowActionButtons();
  scheduleHideActionButtons();
}

function hideActionButtons() {
  if (
    isMessageHovered.value
    || optionMenuOpen.value
    || !actionButtonsVisible.value
  ) {
    return;
  }

  conversationMessageStore.setActiveMessageActionsId(undefined);
}

function scheduleHideActionButtons() {
  cancelHideActionButtons();
  hideActionButtonsTimeout = setTimeout(hideActionButtons, hideDelayMs);
}

function scheduleShowActionButtons() {
  isMessageHovered.value = true;
  cancelHideActionButtons();
  cancelShowActionButtons();
  showActionButtonsTimeout = setTimeout(showActionButtons, showDelayMs);
}

function onEmojiClicked() {
  conversationMessageStore.setActiveMessageActionsId(undefined);
}

onUnmounted(() => {
  cancelHideActionButtons();
  cancelShowActionButtons();
  if (actionButtonsVisible.value) {
    conversationMessageStore.setActiveMessageActionsId(undefined);
  }
});

provide('handleOptionMenuToggled', handleOptionMenuToggled);
</script>

<template>
  <div
    class="relative w-full"
    @mouseenter="scheduleShowActionButtons"
    @mouseleave="onMouseLeave"
  >
    <div
      v-show="actionButtonsVisible"
      class="sticky top-12 z-50 h-0"
    >
      <MessageActions
        :message-state="messageState"
        :max-reactions="maxReactions"
        @message-actions-mouse-enter="cancelHideActionButtons"
        @message-actions-mouse-leave="scheduleHideActionButtons"
        @emoji-clicked="onEmojiClicked"
      />
    </div>
    <slot />
  </div>
</template>
